import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles'
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import UploadIcon from '@mui/icons-material/Upload'

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import { ComposedChart, Scatter, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';
import { processTrk, trackStats } from '../lib/labradar.js'
import toFixed from '../lib/toFixed.js'

const colors = [
  "#FF5733", // vibrant orange.
  "#C70039", // deep crimson.
  "#FFC300", // bright yellow.
  "#74C365", // medium grass green.
  "#116466", // deep teal.
  "#2C3E50", // navy blue.
  "#8E44AD", // royal purple.
  "#F1C40F", // golden yellow.
  "#E74C3C", // bright red.
  "#2ECC71", // vibrant green.
  "#3498DB", // bright blue.
  "#9B59B6", // muted purple.
  "#34495E", // dark blue.
  "#A3E4D7", // soft turquoise.
  "#F39C12", // burnt orange.
  "#7FB3D5", // light sky blue.
]

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

function Labradar() {
  const [pressure, setPressure] = useAutoSaveState('pressure', "")
  const [temperature, setTemperature] = useAutoSaveState('temperature', "")
  const [weight, setBulletWeight] = useAutoSaveState('weight', "")
  const [caliber, setBulletDiameter] = useAutoSaveState('caliber', "")
  const [startRange, setStartRange] = useAutoSaveState('startRange', "15")
  const [endRange, setEndRange] = useAutoSaveState('endRange', "100")
  const [minSnr, setMinSnr] = useAutoSaveState('minSnr', "15")
  const [maxError, setMaxError] = useAutoSaveState('maxError', "5")
  const [csvs, setCsvs] = useAutoSaveState('csvs', [])
  const [tracks, setTracks] = useAutoSaveState('tracks', [])
  const [ preview, setPreview ] = useAutoSaveState('preview', null)
  const [stats, setStats] = useState(null)

  const handleCsvUpload = e => {
    console.log(e)
    for (let i = 0; i < e.target.files.length; ++i) {
      const file = e.target.files[i]
      const reader = new FileReader()
      reader.addEventListener('load', () => {
        setCsvs(csvs => [ ...csvs, reader.result ])
      })
      reader.readAsText(file)
    }
  }

  useEffect(() => {
    const tracks = []
    for (let i = 0; i < csvs.length; ++i) {
      const csv = csvs[i]
      const data = processTrk( csv, parseFloat(pressure), parseFloat(temperature), parseFloat(weight), parseFloat(caliber), parseFloat(minSnr), parseFloat(startRange), parseFloat(endRange))
      console.log({ data })
      data.id = i + 1
      if (data.errorPercent > (maxError / 100)) {
        console.log(data.errorPercent, maxError / 100)
        data.filtered = true
      }
      tracks.push(data)
    }
    const stats = trackStats(tracks)
    let combinedTrack = []
    tracks.forEach(track => {
      let diff = track.v0 - stats.v0Avg
      const shifted = track.data.filter(v => v.dist > 0).map(v => ({ ...v, vel: v.vel - diff }))
      combinedTrack = [ ...combinedTrack, ...shifted ]
    })
    combinedTrack.sort((a, b) => a.dist - b.dist)
    const combinedData = processTrk(combinedTrack, parseFloat(pressure), parseFloat(temperature), parseFloat(weight), parseFloat(caliber), parseFloat(minSnr), parseFloat(startRange), parseFloat(endRange))

    // this needs some more research before we display it
    console.log({ combinedData, combinedTrack })

    setTracks(tracks)
    setStats(stats)
    setPreview(`V0: ${toFixed(stats.v0Avg, 0)}, BC: ${toFixed(stats.bcAvg, 3)}`)
    window.gtag('event', 'calculate-labradar')
  }, [ csvs, pressure, temperature, weight, caliber, minSnr, maxError, startRange, endRange ])

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" style={{ textAlign: 'center' }}>
            Labradar BC Calculator
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }} elevation={6}>
            <Grid container spacing={2} p={2}>
              <Grid item xs={12}>
                <Button fullWidth component="label" variant="contained" startIcon={<UploadIcon />}>
                  Upload files
                  <VisuallyHiddenInput type="file" multiple onChange={handleCsvUpload} />
                </Button>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label={`Pressure`} value={pressure} onChange={e => setPressure(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">inHg</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label={`Temperature`} value={temperature} onChange={e => setTemperature(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">°F</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label={`Bullet Weight`} value={weight} onChange={e => setBulletWeight(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">grains</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label={`Bullet Diameter`} value={caliber} onChange={e => setBulletDiameter(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label={`Start Range`} value={startRange} onChange={e => setStartRange(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">yards</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label={`End Range`} value={endRange} onChange={e => setEndRange(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">yards</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label={`Minimum SNR`} value={minSnr} onChange={e => setMinSnr(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label={`Maximum Error`} value={maxError} onChange={e => setMaxError(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {stats && <Grid item xs={12} sm={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }} elevation={6}>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h2" style={{ textAlign: 'center', fontWeight: 'bold' }} mt={1} mb={1}>
                  V<sub>0</sub> = {toFixed(stats.v0Avg, 0)}
                </Typography>
              </Grid>
              <Grid item xs={6} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                <Typography variant="h5">
                  SD = {toFixed(stats.v0Sd, 1)}
                </Typography>
              </Grid>
              <Grid item xs={6} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                <Typography variant="h5">
                  ES = {toFixed(stats.v0Es, 1)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>}
        {stats && <Grid item xs={12} sm={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }} elevation={6}>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h2" style={{ textAlign: 'center', fontWeight: 'bold' }} mt={1} mb={1}>
                  BC<sub>G7</sub> = {toFixed(stats.bcAvg, 3)}
                </Typography>
              </Grid>
              <Grid item xs={4} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                <Typography variant="h5">
                  SD = {stats.bcSd}
                </Typography>
              </Grid>
              <Grid item xs={4} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                <Typography variant="h5">
                  ES = {stats.bcEs}
                </Typography>
              </Grid>
              <Grid item xs={4} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                <Typography variant="h5">
                  I<sub>7</sub> = {toFixed(stats.ffAvg, 3)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>}
        <Grid item xs={12}>
          <Paper elevation={6}>
            <Box pt={1} pb={1}>
              <TableContainer elevation={6}>
                <Table aria-label="simple table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 'bold' }} align="center">Shot ID</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }} align="center">v0</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }} align="center">v100</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }} align="center">Δv</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }} align="center">ERR</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }} align="center">R<sup>2</sup></TableCell>
                      <TableCell style={{ fontWeight: 'bold' }} align="center">BC<sub>G7</sub></TableCell>
                      <TableCell style={{ fontWeight: 'bold' }} align="center">I<sub>7</sub></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tracks.map((track) => (
                      <TableRow
                        key={track.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          textDecoration: track.filtered ? 'line-through' : '',
                          color: track.filtered ? 'lightgray' : ''
                        }}
                      >
                        <TableCell sx={{ color: track.filtered ? 'lightgray' : '' }} align="center">{track.id}</TableCell>
                        <TableCell sx={{ color: track.filtered ? 'lightgray' : '' }} align="center">{toFixed(track.v0, 1)}</TableCell>
                        <TableCell sx={{ color: track.filtered ? 'lightgray' : '' }} align="center">{toFixed(track.v100, 1)}</TableCell>
                        <TableCell sx={{ color: track.filtered ? 'lightgray' : '' }} align="center">{toFixed(track.v0 - track.v100, 1)}</TableCell>
                        <TableCell sx={{ color: track.filtered ? 'lightgray' : '' }} align="center">{toFixed(track.errorPercent * 100, 1)}</TableCell>
                        <TableCell sx={{ color: track.filtered ? 'lightgray' : '' }} align="center">{toFixed(track.rSquared, 3)}</TableCell>
                        <TableCell sx={{ color: track.filtered ? 'lightgray' : '' }} align="center">{toFixed(track.bc, 3)}</TableCell>
                        <TableCell sx={{ color: track.filtered ? 'lightgray' : '' }} align="center">{toFixed(track.ff, 3)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }} elevation={6}>
            <ResponsiveContainer width="100%" height={800}>
              <ComposedChart
                margin={{
                  top: 20, right: 20, bottom: 20, left: 20,
                }}
              >
                <CartesianGrid />
                <XAxis type="number" dataKey="yards" name="Yards" domain={[ 0, 120 ]} />
                <YAxis type="number" dataKey="fps" name="Velocity" domain={[ stats ? stats.vMin - 5 : 0, stats ? stats.vMax + 5 : 3000 ]}/>
                <Legend />
                {tracks.map((track, i) =>
                  <>
                    <Scatter
                      name={`Shot ${track.id}`}
                      data={track.data.map(v => ({ yards: v.dist, fps: v.vel }))}
                      fill={track.filtered ? 'rgba(200, 200, 200, .5)' : colors[i %  colors.length]}
                    />
                    <Line
                      legendType="none"
                      dataKey="fps"
                      data={[ { yards: 0, fps: track.v0 }, { yards: 100, fps: track.v100 } ]}
                      stroke={colors[i %  colors.length]}
                    />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default function WrappedLabradar () {
  return (
    <AutoSaveProvider>
      <Labradar />
    </AutoSaveProvider>
  )
}
